import { milestoneProgress, milestoneScore } from "../../../shared/BoatTree";
import { BOAT } from "../../../shared/Config";

/**
 * The growth corner: how far the ship is from its next level, and which level it is on.
 *
 * **A readout, not a control.** It used to be the upgrade panel — a bar, a badge of banked
 * points and two rows of buttons — and the whole choosing half of it is gone: growth is
 * automatic in the water and the choosing happens ashore, in the shop (design/Evolution.md,
 * design/Shop.md). What is left is the part a captain actually reads, which was never the
 * buttons.
 *
 * **DOM chrome, not Pixi**, by the dividing question in design/UI.md: none of this has a
 * position in the sea. It is a bar and a number.
 *
 * **It keeps the pointer guard even with no buttons on it**, and that is deliberate rather than
 * leftover. The panel still overlays a sea that steers and fires, so it is `pointer-events: none`
 * — with nothing set back to `auto`, which is the correct end state — and `BattleScene` still
 * ignores any pointer event that landed inside `.growth`. Without it a `pointermove` over the bar
 * would yank the heading toward the top-left corner.
 *
 * The **real** feedback is not this panel. It is the ship changing on the water: visibly bigger,
 * seeing further, with two guns that reload faster than they did a moment ago. This says only
 * how far away the next one is, which is the one thing the water cannot.
 *
 * Styles live in the `<style>` block of `src/index.html`; there is no CSS pipeline.
 */
export class Growth {
    private readonly _root = document.createElement("div");
    private readonly _bar = document.createElement("div");
    private readonly _fill = document.createElement("span");
    private readonly _reading = document.createElement("span");
    private readonly _level = document.createElement("span");

    /** What the level badge was last set to, so a text node is not rewritten sixty times a second. */
    private _shown = -1;

    /**
     * Whether the panel is on screen at all. Down until the player's own hull has actually
     * spawned: a progress bar reading `0 / 200` over a connecting screen is chrome describing a
     * ship that does not exist yet.
     */
    public set visible(value: boolean) {
        this._root.hidden = !value;
    }

    constructor() {
        this._root.className = "growth";

        this._bar.className = "growth-bar";
        this._fill.className = "growth-fill";
        this._reading.className = "growth-reading";
        this._bar.append(this._fill, this._reading);

        // The level a captain is on. Unlike the points badge it replaced, it is never hidden:
        // "level 0" is a true and useful thing to be told, where "0 points banked" was chrome
        // telling the player about something they did not have.
        this._level.className = "growth-level";

        const meter = document.createElement("div");
        meter.className = "growth-meter";
        meter.append(this._bar, this._level);

        this._root.append(meter);
    }

    public mount(parent: HTMLElement = document.body): void {
        parent.appendChild(this._root);
    }

    public destroy(): void {
        this._root.remove();
    }

    /**
     * Brings the panel up to date with the player's own ship. Called every frame, because a
     * score moves whenever a coin is taken.
     *
     * The bar is measured from the **previous** threshold rather than from zero, so it means the
     * same thing at every level: the gaps widen as a run goes on, and a bar measured from zero
     * would fill more and more slowly and tell the player nothing (`milestoneProgress`).
     *
     * Everything here is cheap — a transform, and two strings that are compared before they are
     * assigned. The panel that used to live here rebuilt a row of renderer extracts and had to
     * be careful about when; this one does not.
     */
    public update(score: number, level: number): void {
        this._fill.style.transform = `scaleX(${milestoneProgress(BOAT.milestones, level, score)})`;

        const reading = `${Math.round(score)} / ${milestoneScore(BOAT.milestones, level + 1)}`;
        if (this._reading.textContent !== reading) this._reading.textContent = reading;

        if (level !== this._shown) {
            this._shown = level;
            this._level.textContent = `L${level}`;
        }
    }
}
